import React, {useContext, useId, useRef, useState} from 'react';

import moment from 'moment';
import {mapDateFormat} from 'src/utils/mapDateFormat';

import {FlatButton} from '@pb/kit-component-button';
import {Calendar} from '@pb/kit-component-calendar';
import {Popover} from '@pb/kit-component-dropdown';
import Icon, {Icons} from '@pb/kit-component-icon';
import Label from '@pb/kit-component-label';
import {Message} from '@pb/kit-component-message';
import {LocaleContext} from '@pb/kit-context-locale';

import {RANGE_PRESETS, formatPeriod, getPresetRange, getRangeError, minimumDate, toCalendarDate, toDateFilter} from './chart-period';

import * as styles from './user-history-chart.module.styl';

import type {ChartRange} from './chart-period';

type DateField = 'startDate' | 'endDate';
type Props = {value: ChartRange, today: string, onChange: (range: ChartRange) => void, disabled?: boolean};

export const ChartRangeControl = ({value, today, onChange, disabled = false}: Props) => {
    const {locale} = useContext(LocaleContext);
    const id = useId();
    const [shown, setShown] = useState(false);
    const [draft, setDraft] = useState(value);
    const draftRef = useRef(value);
    const typed = useRef<Partial<Record<DateField, string>>>({});
    const error = getRangeError(draft, today);
    const invalidDates = {
        startDate: Boolean(getRangeError({startDate: draft.startDate, endDate: draft.startDate}, today)),
        endDate: Boolean(getRangeError({startDate: draft.endDate, endDate: draft.endDate}, today)),
    };
    const isInverted = !invalidDates.startDate && !invalidDates.endDate && draft.startDate > draft.endDate;
    const update = (field: DateField, date: string, apply: boolean) => {
        const next = {...draftRef.current, [field]: date};
        const hasChanged = next.startDate !== value.startDate || next.endDate !== value.endDate;
        next.preset = hasChanged ? undefined : value.preset;
        draftRef.current = next;
        setDraft(next);
        if (apply && hasChanged && !getRangeError(next, today)) {
            onChange(next);
        }
    };
    const parseTyped = (raw: string) => {
        const date = moment(raw.replace(/\//g, '-'), mapDateFormat(locale.dateFormat, 'NUMERIC'), true);
        return date.isValid() ? date.format('YYYY-MM-DD') : '';
    };
    const toggle = (open: boolean) => {
        if (open) {
            draftRef.current = value;
            typed.current = {};
            setDraft(value);
        }
        setShown(open);
    };

    return (
        <Popover
            className={styles.periodMenu}
            shown={shown}
            setShown={toggle}
            role="dialog"
            aria-label="Seat allocation date range"
            allowedPlacements={['bottom-end', 'bottom-start']}
            clickEnabled={!disabled}
            hoverEnabled={false}
            arrowEnabled={false}
            maxWidth={336}
            defaultAnchorProps={{
                disabled,
                className: styles.control,
                variant: 'secondary',
                startIcon: <Icon type={Icons.CALENDAR} size="Size_14"/>,
                children: (
                    <>
                        <span>{value.preset || 'Custom range'}</span>{' '}
                        <span className={styles.controlLabel}>
                            ({formatPeriod(
                                value.startDate, value.endDate, value.startDate.slice(0, 4) !== today.slice(0, 4),
                            )})
                        </span>
                    </>
                ),
            }}>
            <div className={styles.rangePopover}>
                <div className={styles.quickRanges}>
                    <div className={styles.rangeHeading}>QUICK RANGES</div>
                    {RANGE_PRESETS.map(preset => (
                        <FlatButton
                            key={preset}
                            className={styles.preset}
                            aria-pressed={value.preset === preset}
                            variant="secondary"
                            onClick={() => {
                                onChange(getPresetRange(preset, today));
                                setShown(false);
                            }}>
                            {preset}
                        </FlatButton>
                    ))}
                </div>
                <div className={styles.customRange}>
                    <div className={styles.rangeHeading}>CUSTOM RANGE</div>
                    {(['startDate', 'endDate'] as const).map(field => (
                        <div key={field} className={styles.dateField}>
                            <Label htmlFor={`${id}-${field}`}>{field === 'startDate' ? 'From' : 'To'}</Label>
                            <Calendar
                                id={`${id}-${field}`}
                                calendarClassName={styles.calendarMenu}
                                dateFormat={mapDateFormat(locale.dateFormat, 'CALENDAR')}
                                selected={toCalendarDate(draft[field])}
                                minDate={toCalendarDate(minimumDate(today))}
                                maxDate={toCalendarDate(today)}
                                filterDate={date => {
                                    const day = toDateFilter(date);
                                    return field === 'startDate'
                                        ? invalidDates.endDate || day <= draft.endDate
                                        : invalidDates.startDate || day >= draft.startDate;
                                }}
                                inputProps={{color: invalidDates[field] || isInverted ? 'ERROR' : 'DEFAULT'}}
                                startDate={toCalendarDate(draft.startDate)}
                                endDate={toCalendarDate(draft.endDate)}
                                calendarStartDay={1}
                                withRangeHint={false}
                                isClearable={true}
                                onChangeRaw={event => {
                                    if (event?.type === 'change' && event.target instanceof HTMLInputElement) {
                                        typed.current[field] = parseTyped(event.target.value);
                                        update(field, typed.current[field]!, false);
                                    }
                                }}
                                onBlur={event => {
                                    if (event.target instanceof HTMLInputElement) {
                                        typed.current[field] = parseTyped(event.target.value);
                                        update(field, typed.current[field]!, true);
                                    }
                                }}
                                onChange={date => {
                                    const day = toDateFilter(date);
                                    if (date && typed.current[field] !== undefined && typed.current[field] !== day) {
                                        return;
                                    }
                                    update(field, day, true);
                                }}
                                onSelect={(date, event) => {
                                    if (event?.type !== 'change') {
                                        delete typed.current[field];
                                        update(field, toDateFilter(date ?? undefined), true);
                                    }
                                }}
                            />
                        </div>
                    ))}
                    {error && draft.startDate && draft.endDate && !isInverted
                        ? <Message color="error">{error}</Message> : null}
                </div>
            </div>
        </Popover>
    );
};
